import styled, { css } from "styled-components";
import { FC, MouseEvent, useState } from "react";
import { useSelector } from "react-redux";
import { Line } from "react-chartjs-2";
import { AnimatePresence } from "framer-motion";
import { InfiniteData, useQueryClient } from "@tanstack/react-query";

import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Filler,
} from "chart.js";

import { ActionsWrap, Td, Topics, TopicsList } from "@/components/Table";
import Tag from "@/components/Tag";
import { RootState } from "@/utils/store";
import { ReactComponent as Pin } from "@/assets/icons/pin.svg";
import { ReactComponent as Pencil } from "@/assets/icons/pencil-with-background.svg";
import { ReactComponent as TrendingUp } from "@/assets/icons/trending-up.svg";
import { ReactComponent as TrendingDown } from "@/assets/icons/trending-down.svg";
import { ReactComponent as TrashIcon } from "@/assets/icons/trashbin.svg";
import { PaginatedResponse, TopTopicsResource } from "@/types/resources";
import { numFormatter } from "@/utils/helpers";
import useTrendGraph from "@/hooks/useTrendGraph";
import { Color } from "@/utils/theme";
import ActionsDropdown, {
  ActionsDropdownButton,
} from "@/components/ActionsDropdown";
import { ReactComponent as PencilIcon } from "@/assets/icons/pencil.svg";
import TopicEditModal from "./TopicEditModal";
import useAlerts from "@/hooks/useAlerts";
import { api } from "@/utils/api";
import RestrictedContent from "@/components/RestrictedContent";
import { TopicsLevel, TopicsMaRatingEnum } from "@/utils/enums";
import TopTopicsInnerTable from "./TopTopicsInnerTable";
import ScrollableTable from "@/components/ScrollableTable";
import AiButton from "./AiButton";
import AiSuggestionsModal from "./AiSuggestionsModal";

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Filler);

export const SyledPin = styled(Pin)`
  margin-right: 4px;
  margin-right: 4px;
  margin-bottom: -3px;
`;

const StyledTd = styled(Td)`
  vertical-align: middle !important;
`;

const Graph = styled.div`
  width: 112px;
`;

const Trending = styled.div`
  margin-top: 8px;
  svg {
    margin-left: 6px;
  }
`;

const Percentage = styled.span<{ $color: Color }>`
  font-size: 11px;
  line-height: 15px;
  font-weight: 700;
  margin-left: 4px;
  color: ${({ $color, theme }) => theme.colors[$color]};
`;

export const ExpandButton = styled.button<{ $open: boolean }>`
  background: ${({ theme }) => theme.colors.grayscale};
  width: 14px;
  height: 14px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 16px;
  flex-shrink: 0;
  svg {
    transition: transform ease-out 0.2s;
  }
  ${({ $open }) =>
    $open &&
    css`
      svg {
        transform: rotate(180deg);
      }
    `}
`;

const InnerTableTd = styled.td`
  padding: 0 !important;
`;

interface Props {
  topic: TopTopicsResource;
  optionsKey: string;
  category: string;
  topicsQueryKey: Array<any>;
  activeTab: number;
  filteredChannelsIds: number[];
  level?: TopicsLevel;
}

const TopTopicsTableRow: FC<Props> = ({
  topic,
  optionsKey,
  category,
  topicsQueryKey,
  activeTab,
  filteredChannelsIds,
  level,
}) => {
  const [showTopicEditModal, setShowTopicEditModal] = useState(false);

  const [showVideosTable, setShowVideosTable] = useState(false);

  const [showAiModal, setShowAiModal] = useState(false);

  const percentage = (() => {
    const { previousTotal, total } = topic.trendingViews;
    if (!previousTotal) return 0;

    return Math.round(((total - previousTotal) / previousTotal) * 100);
  })();

  const { graphData, color, graphOptions } = useTrendGraph(
    Object.keys(topic.trendingViews.data),
    Object.values(topic.trendingViews.data),
    percentage
  );

  const tableViewOptions = useSelector(
    (rootState: RootState) => rootState.options.tableViews[optionsKey]
  );

  const tableViewOptionsArray = Object.keys(tableViewOptions).map(
    (key) => tableViewOptions[key]
  );

  const alerts = useAlerts();

  const queryClient = useQueryClient();

  const onDelete = async (e: MouseEvent<HTMLButtonElement>) => {
    try {
      e.stopPropagation();

      const confirmed = await alerts.confirm(
        `Are you sure you want to delete this topic?`,
        topic.title
      );

      if (!confirmed) return;

      const { id } = await api.delete<never, { id: number }>(
        `/v1/video-topics/${topic.id}`
      );

      const queryData =
        queryClient.getQueryData<
          InfiniteData<PaginatedResponse<TopTopicsResource>>
        >(topicsQueryKey)!;

      const newQueryData = {
        ...queryData,
        pages: queryData.pages.map((page) => ({
          ...page,
          data: page.data.filter((topic) => topic.id !== id),
        })),
      };

      queryClient.setQueryData(topicsQueryKey, newQueryData);

      alerts.success("topic was successfully deleted");
    } catch (err) {
      alerts.error();
    }
  };

  const renderMaRating = () => {
    switch (topic.maRating) {
      case TopicsMaRatingEnum.NA:
        return "n/a";
      case TopicsMaRatingEnum.VERY_BAD:
        return "Very bad";
      case TopicsMaRatingEnum.BAD:
        return "Bad";
      case TopicsMaRatingEnum.GOOD:
        return "Good";
      case TopicsMaRatingEnum.VERY_GOOD:
        return "Very good";
    }
  };

  return (
    <>
      <tr>
        <Td>
          <Topics $hasTopics={true}>
            <ExpandButton
              $open={showVideosTable}
              onClick={() => setShowVideosTable((prev) => !prev)}
            >
              <svg
                width="8"
                height="6"
                viewBox="0 0 8 6"
                fill="none"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  d="M1 1.5L4 4.5L7 1.5"
                  stroke="#4136CF"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </ExpandButton>
            <Pencil />
            <TopicsList>
              <Tag>{topic.title}</Tag>
            </TopicsList>
          </Topics>
        </Td>
        {tableViewOptions["AI Suggestions"] && (
          <Td>
            <AiButton onOpenModal={() => setShowAiModal(true)} />
          </Td>
        )}
        {tableViewOptions["MA Rating"] && <Td>{renderMaRating()}</Td>}
        {tableViewOptions["Total views"] && (
          <Td>{numFormatter(topic.totalViews)}</Td>
        )}
        {tableViewOptions.Videos && <Td>{topic.videosCount}</Td>}
        {tableViewOptions.VPH7 && (
          <Td>{topic.vph7 ? numFormatter(topic.vph7) : "-"}</Td>
        )}
        {tableViewOptions.VPH30 && (
          <Td>{topic.vph30 ? numFormatter(topic.vph30) : "-"}</Td>
        )}
        {tableViewOptions["Combined VPH7"] && (
          <Td>{topic.vph7Sum ? numFormatter(topic.vph7Sum) : "-"}</Td>
        )}
        {tableViewOptions["Combined VPH30"] && (
          <Td>{topic.vph30Sum ? numFormatter(topic.vph30Sum) : "-"}</Td>
        )}
        {tableViewOptions["Average V7"] && (
          <Td>{topic.v7 ? numFormatter(topic.v7) : "-"}</Td>
        )}
        {tableViewOptions["Average V30"] && (
          <Td>{topic.v30 ? numFormatter(topic.v30) : "-"}</Td>
        )}
        {tableViewOptions["V7"] && (
          <Td>{topic.v7Sum ? numFormatter(topic.v7Sum) : "-"}</Td>
        )}
        {tableViewOptions["V30"] && (
          <Td>{topic.v30Sum ? numFormatter(topic.v30Sum) : "-"}</Td>
        )}
        {tableViewOptions["V7 Index"] && (
          <Td>{topic.v7Index?.toFixed(2) ?? "-"}</Td>
        )}
        {tableViewOptions["V30 Index"] && (
          <Td>{topic.v30Index?.toFixed(2) ?? "-"}</Td>
        )}
        {tableViewOptions["Virality Rate"] && (
          <Td>{topic.viralityRate ?? "-"}</Td>
        )}
        {tableViewOptions.RPM && (
          <Td>
            <RestrictedContent>
              {topic.rpm !== null ? `$ ${topic.rpm}` : "-"}
            </RestrictedContent>
          </Td>
        )}
        {tableViewOptions["eYTV (Search)"] && (
          <Td>{topic.searchStats.final_value ?? "-"}</Td>
        )}
        {tableViewOptions["e8min (Search)"] && (
          <Td>
            {topic.searchStats.milestone_views_percentage
              ? `${topic.searchStats.milestone_views_percentage} %`
              : "-"}
          </Td>
        )}
        {tableViewOptions["eYTV 4min (Search)"] && (
          <Td>{topic.searchStats.eytv4 ?? "-"}</Td>
        )}
        {tableViewOptions["e4min (Search)"] && (
          <Td>
            {topic.searchStats.e4min ? `${topic.searchStats.e4min} %` : "-"}
          </Td>
        )}
        {tableViewOptions["ViewRate (Search)"] && (
          <Td>
            {topic.searchStats.view_rate
              ? `${topic.searchStats.view_rate} %`
              : "-"}
          </Td>
        )}
        {tableViewOptions["Rec7"] && (
          <Td>
            {topic.recStats.yt_rec_7
              ? numFormatter(topic.recStats.yt_rec_7)
              : "-"}
          </Td>
        )}
        {tableViewOptions["Rec30"] && (
          <Td>
            {topic.recStats.yt_rec_30
              ? numFormatter(topic.recStats.yt_rec_30)
              : "-"}
          </Td>
        )}
        {tableViewOptions["ARP7"] && (
          <Td>
            {topic.recStats.yt_avg_position_7
              ? (topic.recStats.yt_avg_position_7 * 1).toFixed(2)
              : "-"}
          </Td>
        )}
        {tableViewOptions["ARP30"] && (
          <Td>
            {topic.recStats.yt_avg_position_30
              ? (topic.recStats.yt_avg_position_30 * 1).toFixed(2)
              : "-"}
          </Td>
        )}
        {tableViewOptions["Trending in Views"] && (
          <Td>
            <Graph>
              <Line data={graphData} options={graphOptions} />
            </Graph>
            <Trending>
              Trending
              {percentage > 0 && <TrendingUp />}
              {percentage < 0 && <TrendingDown />}
              <Percentage $color={color}>{percentage} %</Percentage>
            </Trending>
          </Td>
        )}
        {tableViewOptions.Note && <StyledTd>{topic.notes ?? "-"}</StyledTd>}
        <Td $actionsCol>
          <ActionsWrap>
            <ActionsDropdown>
              <ActionsDropdownButton
                as="button"
                onClick={() => setShowTopicEditModal(true)}
              >
                <span>
                  <PencilIcon />
                </span>
                Edit
              </ActionsDropdownButton>
              <ActionsDropdownButton onClick={onDelete} as="button">
                <span>
                  <TrashIcon />
                </span>
                Delete
              </ActionsDropdownButton>
            </ActionsDropdown>
            <AnimatePresence>
              {showTopicEditModal && (
                <TopicEditModal
                  onClose={() => setShowTopicEditModal(false)}
                  topic={topic}
                  category={category}
                  topicsQueryKey={topicsQueryKey}
                />
              )}
              {showAiModal && (
                <AiSuggestionsModal
                  topic={topic.title}
                  topicId={topic.id}
                  onClose={() => setShowAiModal(false)}
                />
              )}
            </AnimatePresence>
          </ActionsWrap>
        </Td>
      </tr>
      {showVideosTable && (
        <tr>
          <InnerTableTd
            colSpan={
              tableViewOptionsArray.filter((item) => item === true).length + 2
            }
          >
            <ScrollableTable>
              <TopTopicsInnerTable
                activeTab={activeTab}
                filteredChannelsIds={filteredChannelsIds}
                topic={topic}
                level={level}
              />
            </ScrollableTable>
          </InnerTableTd>
        </tr>
      )}
    </>
  );
};

export default TopTopicsTableRow;
